import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Divider, Image, Space, Tag, Typography } from 'antd';
import { channelKitApi } from '../video.api';

/** Manual setup steps for platforms whose accounts can't be created via API. */
const SETUP_LINKS: { label: string; href: string; note: string }[] = [
  {
    label: 'Create YouTube channel',
    href: 'https://www.youtube.com/channel_switcher',
    note: 'Make a channel, then connect it in the Integrations tab. Banner + description can be auto-applied.',
  },
  {
    label: 'Create Facebook Page',
    href: 'https://www.facebook.com/pages/create',
    note: 'Create the Page, then connect it in Integrations. Cover, picture & about can be auto-applied.',
  },
  {
    label: 'Create TikTok account',
    href: 'https://www.tiktok.com/signup',
    note: 'Sign up and set the handle/bio/avatar manually — TikTok has no profile API.',
  },
];

export function ChannelKitCard({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['channel-kit', orgId, projectId] as const;

  const query = useQuery({ queryKey: key, queryFn: () => channelKitApi.get(orgId, projectId) });
  const generate = useMutation({
    mutationFn: () => channelKitApi.generate(orgId, projectId),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      message.success('Channel kit ready');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to generate kit'),
  });

  const apply = useMutation({
    mutationFn: (platform: 'youtube' | 'meta') => channelKitApi.apply(orgId, projectId, platform),
    onSuccess: (data) => {
      const applied = data.applied.length
        ? `Applied: ${data.applied.join(', ')}`
        : 'Nothing to apply';
      if (data.warnings.length) {
        message.warning(`${applied}. Skipped: ${data.warnings.join('; ')}`);
      } else {
        message.success(applied);
      }
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to apply'),
  });

  const kit = query.data ?? null;

  return (
    <Card
      size="small"
      title="Channel kit"
      extra={
        canEdit ? (
          <Button
            type={kit ? 'default' : 'primary'}
            size="small"
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {kit ? 'Regenerate' : 'Generate from brand'}
          </Button>
        ) : null
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: kit ? 12 : 0 }}>
        Names, handle, bio, keywords and avatar/banner generated from this project&apos;s brand — to
        set up your YouTube, TikTok and Facebook channels. The accounts can&apos;t be created for
        you (platform rules), but this gives you everything to do it in ~2 minutes.
      </Typography.Paragraph>

      {generate.isPending ? (
        <Typography.Text type="secondary">
          Generating name ideas, copy and images… this takes ~20s.
        </Typography.Text>
      ) : null}

      {kit ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space align="start" wrap size="middle">
            {kit.avatarUrl ? (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Avatar
                </Typography.Text>
                <Image
                  src={kit.avatarUrl}
                  alt="avatar"
                  style={{ maxWidth: 120, borderRadius: '50%', display: 'block' }}
                />
              </div>
            ) : null}
            {kit.bannerUrl ? (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Banner (crop/upscale to 2560×1440 for YouTube)
                </Typography.Text>
                <Image
                  src={kit.bannerUrl}
                  alt="banner"
                  style={{ maxWidth: 320, borderRadius: 8, display: 'block' }}
                />
              </div>
            ) : null}
          </Space>

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Name ideas
            </Typography.Text>
            <Space wrap>
              {kit.names.map((n) => (
                <Tag key={n} style={{ fontSize: 13 }}>
                  <Typography.Text copyable>{n}</Typography.Text>
                </Tag>
              ))}
            </Space>
          </div>

          <Space wrap size="large">
            <span>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Handle:{' '}
              </Typography.Text>
              <Typography.Text copyable>@{kit.handle}</Typography.Text>
            </span>
          </Space>

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Bio (short)
            </Typography.Text>
            <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
              {kit.bio}
            </Typography.Paragraph>
          </div>

          {kit.about ? (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                About (YouTube/Facebook)
              </Typography.Text>
              <Typography.Paragraph copyable style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {kit.about}
              </Typography.Paragraph>
            </div>
          ) : null}

          {kit.keywords.length ? (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Keywords
              </Typography.Text>
              <Typography.Paragraph
                copyable={{ text: kit.keywords.join(', ') }}
                style={{ marginBottom: 0 }}
              >
                {kit.keywords.join(', ')}
              </Typography.Paragraph>
            </div>
          ) : null}

          {canEdit ? (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Apply to a connected account (updates its branding)
                </Typography.Text>
                <Space wrap>
                  <Button
                    size="small"
                    loading={apply.isPending && apply.variables === 'youtube'}
                    onClick={() => apply.mutate('youtube')}
                  >
                    Apply to YouTube
                  </Button>
                  <Button
                    size="small"
                    loading={apply.isPending && apply.variables === 'meta'}
                    onClick={() => apply.mutate('meta')}
                  >
                    Apply to Facebook
                  </Button>
                </Space>
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Needs the account connected with management permission — if it asks, reconnect
                    it in the Integrations tab to grant the new access.
                  </Typography.Text>
                </div>
              </div>
            </>
          ) : null}

          <Divider style={{ margin: '4px 0' }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Set up the accounts (then connect them in the Integrations tab)
          </Typography.Text>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {SETUP_LINKS.map((s) => (
              <div key={s.label}>
                <a href={s.href} target="_blank" rel="noreferrer">
                  {s.label} ↗
                </a>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {' '}
                  — {s.note}
                </Typography.Text>
              </div>
            ))}
          </Space>
        </Space>
      ) : null}
    </Card>
  );
}
